/**
 * SOLID Review
 * S — Strong. One concern: the account on the auth server, without the token ever leaving Infrastructure.
 * O — Good. A new auth call is one method here over the same gateway and token holder.
 * L — Strong. Implements the port interface exactly.
 * I — Good. The port's ten ops — the auth REST surface, which its screens use whole.
 * D — Good. The Infrastructure-internal REST gateway and the token store, both handed in; the Application's port.
 */
import type { IContextRequest } from '../../domain/ports/schemas/ContextRequest';
import type { IPayloadMe } from '../../domain/ports/schemas/SessionMoves';
import type {
  IPayloadEmail, IPayloadSignUp, IPayloadVerifyCode, IPayloadNickname, IPayloadEmailCode, ICommandReferral,
  IPayloadSignedIn, IPayloadProfile, IPayloadReferral,
} from '../../domain/ports/schemas/AuthMoves';
import type { IPortClientAuthServer } from '../../application/ports/contracts';
import type { IGatewayAuthRest, IEntityProfile } from './authApiService';
import { RestApiError } from '../contract/clients';
import { ok } from '../../shared/Result';
import type { TPortResult } from '../../application/ports/PortResult';
import type { SessionTokenStore } from '../storage/SessionTokenStore';

/** The statuses that mean "no player behind this token", not "the server could not be asked". */
const NO_PLAYER: readonly number[] = Object.freeze([401, 404]);
const NOT_SIGNED_IN = { code: 'NOT_SIGNED_IN', message: 'Please sign in first.' } as const;

/**
 * THE AUTH GATEWAY (frontend strict-ports plan, FE2 two ops, FE3 the rest — 2026-10-03).
 *
 * THE TOKEN STAYS HERE. A code accepted is saved to the token holder and answered as WHO signed in; the
 * profile's calls read the token from the holder; signing out clears it. The Application, the Domain and
 * React see the account, never the JWT.
 *
 * FE3 also separates the two ways `me` can come back empty. FE2 moved AppRoot's `.catch(() => null)`
 * unchanged, so an offline reload read as "no player" and signed a registered player out (§28.199). Now a
 * 401 or 404 is "no player" (null), and anything else fails — the entry stops without signing out.
 */
export class ClientAuthServerAdapter implements IPortClientAuthServer {
  constructor(private readonly rest: IGatewayAuthRest, private readonly tokens: SessionTokenStore) {}

  async account(_ctx: IContextRequest) {
    return ok(this.tokens.account());
  }

  async me(_ctx: IContextRequest): Promise<TPortResult<IPayloadMe | null>> {
    const jwt = this.tokens.token();
    if (!jwt) return ok(null);
    try {
      const answer = await this.rest.getMe(jwt);
      const me: IPayloadMe = {
        id: answer.id, displayName: answer.displayName, email: answer.email, matchCredits: answer.matchCredits, isAdmin: answer.isAdmin,
      };
      return ok(me);
    } catch (error) {
      if (error instanceof RestApiError && NO_PLAYER.includes(error.status)) return ok(null);
      return RestApiError.failureOf(error);
    }
  }

  async signOut(_ctx: IContextRequest) {
    this.tokens.clearToken();
    return ok(undefined);
  }

  async requestLoginCode(_ctx: IContextRequest, request: IPayloadEmail): Promise<TPortResult<void>> {
    return RestApiError.resultOf(async () => { await this.rest.requestLoginCode(request.email); });
  }

  async register(_ctx: IContextRequest, request: IPayloadSignUp): Promise<TPortResult<void>> {
    return RestApiError.resultOf(async () => {
      await this.rest.register(request.email, request.displayName, request.referralCode ?? undefined, request.consentAccepted);
    });
  }

  /** A code accepted: the token is kept here, and the answer is who signed in. */
  async verifyCode(_ctx: IContextRequest, request: IPayloadVerifyCode): Promise<TPortResult<IPayloadSignedIn>> {
    try {
      const answer = request.purpose === 'VERIFY_EMAIL'
        ? await this.rest.verifyEmail(request.email, request.code)
        : await this.rest.verifyLoginCode(request.email, request.code);
      this.tokens.saveToken(answer.jwt);
      const identity: IPayloadSignedIn = { playerId: answer.playerId, displayName: answer.displayName, matchCredits: answer.matchCredits };
      return ok(identity);
    } catch (error) {
      return RestApiError.failureOf(error);
    }
  }

  async validateReferral(_ctx: IContextRequest, request: ICommandReferral): Promise<TPortResult<IPayloadReferral>> {
    return RestApiError.resultOf(async (): Promise<IPayloadReferral> => ({ valid: (await this.rest.validateReferral(request.code)).valid }));
  }

  async updateDisplayName(_ctx: IContextRequest, request: IPayloadNickname): Promise<TPortResult<IPayloadProfile>> {
    const jwt = this.tokens.token();
    if (!jwt) return { success: false, error: NOT_SIGNED_IN };
    return RestApiError.resultOf(async () => this.profileOf(await this.rest.updateDisplayName(jwt, request.displayName)));
  }

  async requestEmailChange(_ctx: IContextRequest, request: IPayloadEmail): Promise<TPortResult<void>> {
    const jwt = this.tokens.token();
    if (!jwt) return { success: false, error: NOT_SIGNED_IN };
    return RestApiError.resultOf(async () => { await this.rest.requestEmailChange(jwt, request.email); });
  }

  async verifyEmailChange(_ctx: IContextRequest, request: IPayloadEmailCode): Promise<TPortResult<IPayloadProfile>> {
    const jwt = this.tokens.token();
    if (!jwt) return { success: false, error: NOT_SIGNED_IN };
    return RestApiError.resultOf(async () => this.profileOf(await this.rest.verifyEmailChange(jwt, request.email, request.code)));
  }

  private profileOf(answer: IEntityProfile): IPayloadProfile {
    return { displayName: answer.displayName, email: answer.email, matchCredits: answer.matchCredits };
  }
}
